// =============================================================================
// BILLING TYPES
// =============================================================================

export type InvoiceStatus =
  | "pending"
  | "charged"
  | "paid"
  | "failed"
  | "cancelled"
  | "invoiced" // open Stripe invoice sent for the customer to pay (no card charged)
  | "not_configured";

/**
 * How Stripe collects a charge being created NOW. Maps 1:1 onto the
 * `collectionMethod` field of POST /admin/commission-billing/charge — nothing
 * else belongs in this union.
 */
export type CollectionMethod = "charge_automatically" | "send_invoice";

/**
 * Which endpoint the Charge modal's confirm button hits. `charge_card` posts
 * /charge (carrying a CollectionMethod); `schedule_notice` posts
 * /schedule-invoice-notice, a DIFFERENT endpoint that books the charge, emails
 * the invoice as an advance notice, and takes the card on file a few days
 * later. It is deliberately not a third CollectionMethod: the backend enum has
 * no such value, and the notice has no collection method of its own — the
 * charge that follows always bills the card.
 */
export type BillingAction = "charge_card" | "schedule_notice";

/**
 * Billing arrangements. Mirrors BILLING_TYPES in the backend's
 * commission-billing/constants/billing-type.ts — keep the two in step.
 *
 * `commission_or_flat` = commission with a contractual monthly floor
 * (`minimumFeeAmount`), ENFORCED in the backend charge path since 2026-08. The
 * bill is topped up to the floor only when the month is at/after the account's
 * first full calendar month on the arrangement AND Autumn drove at least the
 * engaged-session share threshold AND the commission came in under the floor —
 * so the UI must not imply the minimum is billed every month either. The charge
 * preview reports the resolved decision for a given period; that is the only
 * place the answer is authoritative.
 */
export const BILLING_TYPES = [
  "fixed",
  "commission",
  "hybrid",
  "commission_or_flat",
] as const;

export type BillingType = (typeof BILLING_TYPES)[number];

export const BILLING_TYPE_LABELS: Record<BillingType, string> = {
  commission: "Commission",
  fixed: "Fixed (Platform Fee)",
  hybrid: "Hybrid (Platform Fee + Commission)",
  commission_or_flat: "Commission or Flat Fee (minimum)",
};

/** Types charged on commissionRate × bookingRevenue. */
export const isCommissionBillingType = (
  t: BillingType | null | undefined,
): boolean => t === "commission" || t === "hybrid" || t === "commission_or_flat";

/** Types that bill platformFeeAmount as a real invoice line. */
export const isPlatformFeeBillingType = (
  t: BillingType | null | undefined,
): boolean => t === "fixed" || t === "hybrid";

export type InvoiceLine = {
  description: string;
  amount: number;
  isDiscount?: boolean;
};

// =============================================================================
// ADD-ONS
// =============================================================================

/** Catalog key for the email-marketing add-on — the only add-on type whose
 *  enablement also drives a customer-facing dashboard surface
 *  (hotel.emailMarketingEnabled). Mirrors the backend addons catalog. */
export const EMAIL_MARKETING_ADDON_TYPE = "email_marketing";

/** Add-on types whose enablement gates a coupled dashboard product surface.
 *  For these, enabling the billing add-on offers to also flip dashboard
 *  visibility. Keyed by addon_type, mirroring the backend. */
const DASHBOARD_VISIBILITY_ADDON_TYPES: ReadonlySet<string> = new Set([
  EMAIL_MARKETING_ADDON_TYPE,
]);

export const isDashboardVisibilityAddon = (type: string): boolean =>
  DASHBOARD_VISIBILITY_ADDON_TYPES.has(type);

/** One client's configuration for a single add-on (generic, keyed by `type`).
 *  Mirrors the backend ClientAddonDto. */
export type ClientAddon = {
  type: string; // catalog key, e.g. "email_marketing"
  displayName: string; // e.g. "Email Marketing Add-On"
  enabled: boolean;
  monthlyPrice: number | null; // dollars
  startDate: string | null; // "YYYY-MM-DD"
  trialEnabled: boolean;
  // Date the 30-day trial ends and billing begins ("YYYY-MM-DD"); null when no
  // trial / no start date. Backend-derived (single source of the 30-day rule).
  trialEndDate?: string | null;
};

/** The set of add-on types the admin can configure on a client, with their
 *  default display name. Mirrors the backend addons catalog so the editor can
 *  offer an add-on the client hasn't configured yet (empty `addons[]`). */
export const ADDON_CATALOG: ReadonlyArray<{ type: string; displayName: string }> =
  [{ type: EMAIL_MARKETING_ADDON_TYPE, displayName: "Email Marketing Add-On" }];

export type ClientBilling = {
  id: string;
  accountCreatedAt?: string; // ISO date the user account was created (User.createdAt)
  userId: string;
  clientName: string;
  hotelName: string;
  email: string; // Auth/login email
  googleAdsCustomerId: string;
  commissionRate: number; // percentage (e.g., 15 = 15%)
  bookingRevenue: number; // total booking value from Google Ads
  commission: number; // calculated: bookingRevenue * commissionRate / 100
  totalAmount?: number | null; // actual total billed (from charge record)
  invoiceStatus: InvoiceStatus;
  stripeCustomerId?: string;
  stripeInvoiceId?: string;
  lastChargeDate?: string;
  // Stripe customer details
  stripeEmail?: string; // Email from Stripe account
  billingName?: string; // Name on billing
  billingAddress?: {
    line1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  paymentMethod?: {
    brand?: string; // visa, mastercard, amex, etc.
    last4?: string; // last 4 digits of card
    expMonth?: number;
    expYear?: number;
  };
  canCharge?: boolean; // From backend - indicates if client can be charged
  warningMessage?: string; // From backend - warning message if canCharge is false
  /**
   * Plan-rate alignment from the charge preview: whether the charged commission
   * rate differs from the rate the client's plan declares. When true, the charge
   * is blocked until the admin acknowledges the override.
   */
  planRateMismatch?: boolean;
  planExpectedRate?: number | null; // the plan's declared rate (decimal, e.g. 0.15)
  planRateStatus?: string; // in_sync | mismatch | no_plan_rate | needs_review | not_applicable
  /**
   * Authoritative total from the charge-preview endpoint:
   * commission (+ platformFee for hybrid) + add-ons === sum(lineItems).
   * Only present on a preview-transformed client; seed the modal total from this
   * rather than re-deriving commission + platformFee (which omits add-ons).
   */
  totalChargeAmount?: number | null;
  // Invoice display config
  billingType?: BillingType | null;
  /**
   * Contractual monthly floor for commission_or_flat clients. ENFORCED in the
   * charge path since 2026-08 — the bill is topped up to this figure when the
   * client is enrolled, the month is at or after their first full calendar month
   * on the arrangement, Autumn drove enough of the month's engaged sessions, and
   * the commission came in under it. Has no Stripe representation: there is no
   * product or price behind the floor, so the Plan column reads it from here.
   */
  minimumFeeAmount?: number | null;
  /**
   * From the billing list: whether the minimum could top up THIS month's bill.
   * The backend sets it when the minimum is switched on platform-wide, the
   * client is on the plan, and the month is at or after their first full
   * calendar month. It is decided without GA4, so true means "may apply" (the
   * engaged-share test still runs at charge time). False means a
   * $0-commission row has nothing to bill from the minimum.
   */
  minimumFeeMayApply?: boolean;
  displayBasePrice?: number | null;
  displayCommissionMarkup?: number | null;
  showOnboardingFee?: boolean | null;
  showReputationManagementWaiver?: boolean | null;
  planDescription?: string | null;
  subscriptionStatus?: string | null;
  platformFeeAmount?: number | null;
  platformFeeDescription?: string | null;
  /** Active add-ons configured for this client (generic, keyed by add-on type).
   *  Empty when the client has no configured add-ons. */
  addons?: ClientAddon[];
  /** hotel.emailMarketingEnabled — whether the email-marketing product is shown
   *  on the customer's own dashboard (distinct from the email_marketing billing
   *  add-on in `addons`). */
  dashboardEmailMarketingEnabled?: boolean | null;
  enrichmentPending?: boolean;
  isTestUser?: boolean;
  /** Invoice line items from backend preview — authoritative list. */
  lineItems?: InvoiceLine[];
  // Manual-override visibility + charge attribution (from charge record)
  isManualOverride?: boolean;
  isCustomAmount?: boolean;
  calculatedAmount?: number | null;
  overrideReason?: string | null;
  createdByName?: string | null;
  chargedAt?: string | null;
  /**
   * From the charge preview: the CUSTOMER's standing collection preference —
   * true when they asked to be emailed their invoice first and charged a few
   * days later. Advisory only: it preselects the notice option in the Charge
   * modal; the admin's explicit choice still wins.
   */
  prefersEmailInvoice?: boolean;
  /**
   * From the charge preview: the date the customer was told their card would be
   * charged ("October 6, 2026"), when this period is already booked. Null when
   * nothing is scheduled. Non-null means they hold an invoice naming that date,
   * so charging now bills them early.
   */
  scheduledChargeOn?: string | null;
  // send_invoice billing (customer pays an open Stripe invoice)
  collectionMethod?: CollectionMethod | null;
  invoicedAt?: string | null;
  invoiceDueAt?: string | null;
  stripeHostedInvoiceUrl?: string | null; // the customer's payment link
  // Re-charge / reset-for-rebill log
  rechargeCount?: number;
  lastRechargedAt?: string | null;
  lastRechargedByName?: string | null;
  // Scheduled automatic card retries (days 4/7/10/13 after a decline, max 4)
  autoRetryCount?: number;
  lastAutoRetryAt?: string | null;
  resetCount?: number;
  lastResetAt?: string | null;
  lastResetByName?: string | null;
};

// =============================================================================
// CONSTANTS
// =============================================================================

export const STATUS_COLORS: Record<InvoiceStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  charged: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-green-100 text-green-800 border-green-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  cancelled: "bg-gray-100 text-gray-800 border-gray-300",
  invoiced: "bg-purple-100 text-purple-800 border-purple-300",
  not_configured: "bg-orange-100 text-orange-800 border-orange-300",
};

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  pending: "Pending",
  charged: "Charged",
  paid: "Paid",
  failed: "Failed",
  cancelled: "Cancelled",
  invoiced: "Invoiced",
  not_configured: "Not Configured",
};

/** Styling for an invoiced row whose due date has passed without payment. */
export const OVERDUE_STATUS_COLOR =
  "bg-rose-100 text-rose-800 border-rose-300";
export const OVERDUE_STATUS_LABEL = "Invoice Overdue";

export const isInvoiceOverdue = (client: {
  invoiceStatus: InvoiceStatus;
  invoiceDueAt?: string | null;
}): boolean =>
  client.invoiceStatus === "invoiced" &&
  !!client.invoiceDueAt &&
  new Date(client.invoiceDueAt).getTime() < Date.now();

// =============================================================================
// STRIPE PLANS TYPES
// =============================================================================

export type StripeSubscription = {
  id: string;
  status: string;
  planDescription: string | null;
  planAmount: number | null;
  planInterval: string | null;
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: number | null;
  created: number | null;
  trialStart: number | null;
  trialEnd: number | null;
};

export type StripeCustomerWithPlan = {
  id: string;
  name: string | null;
  email: string | null;
  userId: string | null;
  commissionRate: number | null;
  billingType?: BillingType | null;
  minimumFeeAmount?: number | null;
  displayBasePrice?: number | null;
  displayCommissionMarkup?: number | null;
  showOnboardingFee?: boolean | null;
  isTestUser?: boolean;
  subscriptions: StripeSubscription[];
};

export type StripePlansResponse = {
  data: StripeCustomerWithPlan[];
  hasMore: boolean;
  nextCursor: string | null;
};

export const SUBSCRIPTION_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-300",
  trialing: "bg-blue-100 text-blue-800 border-blue-300",
  past_due: "bg-yellow-100 text-yellow-800 border-yellow-300",
  canceled: "bg-gray-100 text-gray-800 border-gray-300",
  unpaid: "bg-red-100 text-red-800 border-red-300",
  incomplete: "bg-orange-100 text-orange-800 border-orange-300",
};

export interface BulkChargeJob {
  jobId: string;
  status: "processing" | "completed";
  processed: number;
  total: number;
  succeeded: { userId: string; amount: number; invoiceId: string }[];
  skipped: { userId: string; reason: string }[];
}

export const STATUS_PRIORITY: Record<string, number> = {
  pending: 0,        // Ready to charge - show first
  invoiced: 1,       // Awaiting customer payment - keep visible
  paid: 2,           // Completed this month
  failed: 3,         // Needs retry
  charged: 4,        // Processing
  cancelled: 5,      // Archived
  not_configured: 6, // Not set up - show last
};

export type ReconciliationStatus =
  | "in_sync"
  | "drift"
  | "needs_review"
  | "no_stripe_customer"
  | "no_billing_config"
  | "stripe_error";

export interface BillingReconciliation {
  userId: string;
  stripeCustomerId: string | null;
  appCommissionRate: number | null; // 0–1
  stripeDiscountRates: number[]; // 0–1, distinct
  stripeDiscountSource: "customer" | "subscription" | "both" | null;
  status: ReconciliationStatus;
  suggestedRate: number | null; // 0–1, the single drifting Stripe rate
  suggestedRateSource?: "plan" | "coupon" | null; // where suggestedRate came from
  planRate?: number | null; // 0–1, rate the plan declares (Stripe product metadata), when resolvable
  warnings: string[];
  label?: string; // display name (hotel/owner/email) — populated by the bulk run
}

export interface BulkReconciliation {
  results: BillingReconciliation[];
  summary: {
    total: number;
    inSync: number;
    drift: number;
    needsReview: number;
    error: number;
  };
}

export interface BillingSyncResult {
  applied: boolean;
  previousRate: number | null;
  newRate: number | null;
  reason: ReconciliationStatus;
  reconciliation: BillingReconciliation;
}

// =============================================================================
// TABLE DERIVATIONS (pure — consumed by Billing.tsx)
// =============================================================================

// Filters the admin can apply to the billing table (all client-side).
export type BillingFilters = {
  searchQuery: string;
  statusFilter: InvoiceStatus | "all";
  showReadyOnly: boolean;
  showTestUsers: boolean;
};

export type PlanInfo = { planDescription: string; subscriptionStatus: string };
export type PlanInfoMap = {
  userIdMap: Map<string, PlanInfo>;
  stripeIdMap: Map<string, PlanInfo>;
};

export type SummaryStats = {
  totalClients: number;
  totalRevenue: number;
  totalCommission: number;
  pendingInvoices: number;
  awaitingPaymentInvoices: number;
  paidInvoices: number;
};
